import { useGetChecklistsByAccountIdQuery } from '@/services/checklists';
import { useGetDocumentExtractsByAccountQuery } from '@/services/genai';
import { Button, Box, useBreakpoint } from '@palmetto/palmetto-components';

interface AiAssistantButtonProps {
  accountId: string;
  toggleDrawer: () => void;
  isDrawerOpen?: boolean;
}

export const AiAssistantButton = ({ accountId, toggleDrawer, isDrawerOpen = false }: AiAssistantButtonProps) => {
  const { isPhone } = useBreakpoint();
  const { data: modelOutputs } = useGetDocumentExtractsByAccountQuery(accountId);
  const { data: checklists = [] } = useGetChecklistsByAccountIdQuery(accountId);
  // The badge counts everything the drawer surfaces for this account: AI model outputs + the account's
  // checklists. The assistant icon is shown iff there is something to surface (count > 0). Checklist status
  // does not gate visibility, and a loading/errored checklists query simply contributes 0 (defaults to []),
  // so an unavailable checklists endpoint never hides the assistant.
  const notificationCount = (modelOutputs?.length || 0) + checklists.length;

  // Hide the floating launcher while the drawer is open (the open drawer replaces it; closing it via the ✕
  // brings the icon back), and when there is nothing to surface.
  if (isDrawerOpen || notificationCount === 0) {
    return null;
  }

  return (
    <>
      <Button
        variant="tertiary"
        tone="primary"
        size="md"
        onClick={toggleDrawer}
        aria-label="View AI Assistant"
        title="View AI Assistant"
        style={{ position: 'fixed', bottom: '12px', left: '12px', zIndex: 10001, borderRadius: '50%' }}
      >
        <img src="/ai.svg" height={isPhone ? '24px' : '36px'} />
      </Button>
      {notificationCount > 0 && (
        <Box
          width="20px"
          height="20px"
          alignItems="center"
          justifyContent="center"
          fontSize="xs"
          fontWeight="bold"
          background="info-200"
          style={{
            position: 'fixed',
            borderRadius: '50%',
            bottom: isPhone ? '16px' : '20px',
            left: isPhone ? '42px' : '52px',
            zIndex: 10002,
          }}
        >
          {notificationCount}
        </Box>
      )}
    </>
  );
};
